import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import moment from 'moment';
import { of } from 'rxjs';
import { switchMap, map, tap, take, filter, finalize } from 'rxjs/operators';
import { EnvironmentConfigurationService } from '@nimbus-library/features/configuration';
import { IMap } from '@nimbus-library/features/shared/models/interfaces';
import { DateFormat, Language } from '../models/enums/language.enum';
import { TranslateServiceRegistry } from '../models/translate-service-registry';
import { ILocalizationService } from '../services/i-localization.service';
import { setActiveLocale } from '../utils/active-locale';
import { LocalizationActions } from './localization.action';

export interface LocalizationStateModel {
  language: Language;
  // Last applied language, never Debug and never null while a change is loading; for code that formats or sorts text.
  activeLanguage: Language;
  dateFormat: DateFormat;
  availableDateFormats: DateFormat[];
  availableLanguages: Language[];
  // availableLanguages narrowed to the app's allow-list; availableLanguages stays complete for browser-locale formatting.
  selectableLanguages: Language[];
  locales: moment.Locale[];
}

const DEFAULT_LANGUAGES = [
  Language.English,
  Language.German,
  Language.French,
  Language.Italian,
  Language.Dutch,
  Language.Debug,
  Language.Danish,
  Language.Spanish,
  Language.Swedish,
];

const DEFAULT_DATE_FORMATS = [
  DateFormat.English,
  DateFormat.UK,
  DateFormat.German,
  DateFormat.French,
  DateFormat.Italian,
  DateFormat.Dutch,
  DateFormat.Danish,
  DateFormat.Spanish,
  DateFormat.Swedish,
];

@State<LocalizationStateModel>({
  name: 'localizationState',
  defaults: {
    language: null,
    activeLanguage: Language.English,
    dateFormat: null,
    availableDateFormats: [],
    availableLanguages: [],
    selectableLanguages: [],
    locales: [],
  },
})
@Injectable()
export class LocalizationState {
  private readonly originalLocales: IMap<{ moment: moment.Locale }>;
  private readonly excludedLanguages: string[];
  // Shared by explicit and loaded language changes: only the latest one may apply its translation.
  private latestLanguageRequest = 0;
  private latestExplicitLanguageRequest = 0;
  private explicitLanguagePending = false;
  public constructor(
    private readonly translateServiceRegistry: TranslateServiceRegistry,
    private readonly localizationService: ILocalizationService,
    private readonly dataAdapter: DateAdapter<any>,
    private readonly environmentConfigService: EnvironmentConfigurationService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {
    this.excludedLanguages = this.environmentConfigService?.configurationSettings?.lang_exclude_list;
    this.originalLocales = this.getAvailableDataFormats().reduce(
      (originalLocales, dateFormat) => {
        originalLocales[dateFormat] = { moment: moment.localeData(dateFormat) };
        return originalLocales;
      },
      {} as IMap<{ moment: moment.Locale }>,
    );
  }

  @Selector()
  public static selectedLanguage(state: LocalizationStateModel) {
    return state.language;
  }

  @Selector()
  public static activeLanguage(state: LocalizationStateModel): Language {
    return state.activeLanguage;
  }

  @Selector()
  public static availableLanguages(state: LocalizationStateModel) {
    return state.availableLanguages;
  }

  @Selector()
  public static selectableLanguages(state: LocalizationStateModel): Language[] {
    return state.selectableLanguages;
  }

  @Selector()
  public static selectedDateFormat(state: LocalizationStateModel) {
    return state.dateFormat;
  }

  @Selector()
  public static availableDateFormats(state: LocalizationStateModel) {
    return state.availableDateFormats;
  }

  @Action(LocalizationActions.InitializeLanguages)
  public initializeLanguages({ patchState }: StateContext<LocalizationStateModel>) {
    patchState({
      availableLanguages: this.getAvailableLanguages(),
      selectableLanguages: this.getSelectableLanguages(),
      availableDateFormats: this.getAvailableDataFormats(),
    });
  }

  @Action(LocalizationActions.SetLanguage)
  public setLanguage(
    { dispatch, patchState, getState }: StateContext<LocalizationStateModel>,
    { language }: LocalizationActions.SetLanguage,
  ) {
    language = language || getState().language;
    const request = ++this.latestLanguageRequest;
    this.latestExplicitLanguageRequest = request;
    this.explicitLanguagePending = true;
    patchState({
      language: null,
    });
    return this.translateServiceRegistry.changeLanguage(language).pipe(
      take(1),
      filter(() => request === this.latestLanguageRequest),
      tap(() => {
        this.applyActiveLanguage(language);
        const locales = this.updateLocale(language === Language.Debug ? Language.English : language, getState().availableDateFormats);
        patchState({
          locales,
          activeLanguage: this.toActiveLanguage(language),
        });
        this.updateLibraries(language === Language.Debug ? Language.English : language, getState().dateFormat);
      }),
      switchMap(() => dispatch(new LocalizationActions.SetLanguageSuccess(language))),
      finalize(() => {
        if (request === this.latestExplicitLanguageRequest) {
          this.explicitLanguagePending = false;
        }
      }),
    );
  }

  @Action(LocalizationActions.SetLanguageSuccess)
  public setLanguageSuccess({ patchState }: StateContext<LocalizationStateModel>, { language }: LocalizationActions.SetLanguageSuccess) {
    patchState({
      language,
    });

    return this.localizationService.saveUserLanguage(language);
  }

  @Action(LocalizationActions.SetDateFormat)
  public setDateFormat(
    { dispatch, patchState, getState }: StateContext<LocalizationStateModel>,
    { dateFormat }: LocalizationActions.SetDateFormat,
  ) {
    dateFormat = dateFormat || getState().dateFormat;
    patchState({
      dateFormat: null,
    });
    return dispatch(new LocalizationActions.SetDateFormatSuccess(dateFormat));
  }

  @Action(LocalizationActions.SetDateFormatSuccess)
  public setDateFormatSuccess(
    { patchState, getState }: StateContext<LocalizationStateModel>,
    { dateFormat }: LocalizationActions.SetDateFormatSuccess,
  ) {
    const locales = this.updateLocale(getState().language, getState().availableDateFormats);

    patchState({
      dateFormat,
      locales,
    });

    this.updateLibraries(getState().language, dateFormat);
    return this.localizationService.saveUserDateFormat(dateFormat);
  }

  @Action(LocalizationActions.LoadDateFormat)
  public loadDateFormat({ patchState, dispatch, getState }: StateContext<LocalizationStateModel>, {}: LocalizationActions.LoadLanguage) {
    patchState({
      dateFormat: null,
    });
    const availableDateFormats = this.getAvailableDataFormats();
    return this.localizationService.loadUserDateFormat().pipe(
      map((originalDateFormat) => ({
        originalDateFormat,
        selectedDateFormat: availableDateFormats.includes(originalDateFormat)
          ? originalDateFormat
          : this.localizationService.getBrowserDateformat(getState().availableDateFormats, DateFormat.English),
      })),
      switchMap(({ originalDateFormat, selectedDateFormat }) =>
        dispatch(new LocalizationActions.LoadDateFormatSuccess(selectedDateFormat)).pipe(
          switchMap(() =>
            originalDateFormat !== selectedDateFormat ? this.localizationService.saveUserDateFormat(selectedDateFormat) : of(undefined),
          ),
        ),
      ),
    );
  }

  @Action(LocalizationActions.LoadDateFormatSuccess)
  public loadDateFormatSuccess(
    { patchState, getState }: StateContext<LocalizationStateModel>,
    { dateFormat }: LocalizationActions.LoadDateFormatSuccess,
  ) {
    const appliedDateFormat = this.getAvailableDataFormats().includes(dateFormat) ? dateFormat : DateFormat.English;
    const locales = this.updateLocale(getState().language, getState().availableDateFormats);

    patchState({
      dateFormat: appliedDateFormat,
      locales,
    });
  }

  @Action(LocalizationActions.LoadLanguage)
  public loadLanguage({ patchState, dispatch, getState }: StateContext<LocalizationStateModel>, {}: LocalizationActions.LoadLanguage) {
    patchState({
      language: null,
    });
    const stateSelectableLanguages = getState().selectableLanguages;
    const selectableLanguages = stateSelectableLanguages?.length ? stateSelectableLanguages : this.getSelectableLanguages();
    const explicitRequestAtLoad = this.latestExplicitLanguageRequest;
    return this.localizationService.loadUserLanguage().pipe(
      map((originalLanguage) => ({
        originalLanguage,
        selectedLanguage: selectableLanguages.includes(originalLanguage)
          ? originalLanguage
          : this.localizationService.getBrowserLanguage(selectableLanguages, Language.English),
      })),
      switchMap(({ originalLanguage, selectedLanguage }) =>
        dispatch(new LocalizationActions.LoadLanguageSuccess(selectedLanguage)).pipe(
          switchMap(() =>
            // Only persist the fallback if no explicit choice started meanwhile or is still loading.
            originalLanguage !== selectedLanguage &&
            this.latestExplicitLanguageRequest === explicitRequestAtLoad &&
            !this.explicitLanguagePending
              ? this.localizationService.saveUserLanguage(selectedLanguage)
              : of(undefined),
          ),
        ),
      ),
    );
  }

  // The admin service emits the cached language and then the server one; the later value must win,
  // and an explicit choice still loading its translation beats both.
  @Action(LocalizationActions.LoadLanguageSuccess)
  public loadLanguageSuccess(
    { patchState, getState }: StateContext<LocalizationStateModel>,
    { language }: LocalizationActions.LoadLanguageSuccess,
  ) {
    if (this.explicitLanguagePending) {
      return;
    }
    const request = ++this.latestLanguageRequest;
    const appliedLanguage = this.getAvailableLanguages().includes(language) ? language : Language.English;
    return this.translateServiceRegistry.changeLanguage(appliedLanguage).pipe(
      take(1),
      filter(() => request === this.latestLanguageRequest),
      tap(() => {
        this.applyActiveLanguage(appliedLanguage);
        const libraryLanguage = appliedLanguage === Language.Debug ? Language.English : appliedLanguage;
        const locales = this.updateLocale(libraryLanguage, getState().availableDateFormats);
        this.updateLibraries(libraryLanguage, getState().dateFormat);
        patchState({
          language: appliedLanguage,
          activeLanguage: this.toActiveLanguage(appliedLanguage),
          locales,
        });
      }),
    );
  }

  private getAvailableLanguages(): Language[] {
    return this.excludedLanguages?.length ? DEFAULT_LANGUAGES.filter((lang) => !this.excludedLanguages.includes(lang)) : DEFAULT_LANGUAGES;
  }

  private getSelectableLanguages(): Language[] {
    const supported = this.localizationService.getSupportedLanguages();
    const languages = this.getAvailableLanguages();
    return supported ? languages.filter((lang) => lang === Language.Debug || supported.includes(lang)) : languages;
  }

  private getAvailableDataFormats(): DateFormat[] {
    return this.excludedLanguages?.length
      ? DEFAULT_DATE_FORMATS.filter((lang) => !this.excludedLanguages.includes(lang as DateFormat))
      : DEFAULT_DATE_FORMATS;
  }

  private updateLocale(language: string, availableDateFormats: DateFormat[]): moment.Locale[] | undefined {
    if (!!language && language !== Language.Debug) {
      const baseLocale = this.originalLocales[language].moment;
      // defineLocale also switches moment's global locale; keep it where it was.
      const globalLocale = moment.locale();
      const locales = availableDateFormats.map((dateFormat: string): moment.Locale => {
        dateFormat = dateFormat.toLowerCase();
        return moment.defineLocale(dateFormat, {
          parentLocale: dateFormat,
          months: baseLocale.months(),
          monthsShort: baseLocale.monthsShort(),
          weekdays: baseLocale.weekdays(),
          weekdaysShort: baseLocale.weekdaysShort(),
        });
      });
      moment.locale(globalLocale);
      return locales;
    }
  }

  private updateLibraries(language: string, dateFormat: string): void {
    this.dataAdapter.setLocale(language);
  }

  private toActiveLanguage(language: Language): Language {
    return !language || language === Language.Debug ? Language.English : language;
  }

  private applyActiveLanguage(language: Language): void {
    setActiveLocale(this.toActiveLanguage(language));
    if (language && language !== Language.Debug) {
      this.document.documentElement.lang = language;
    }
  }
}
